import React, { useEffect, useState, useContext, useLayoutEffect } from 'react';
import {
  AppHeader,
  Button,
  ButtonGroup,
  CircularProgress,
  Box,
  Container,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { getNextPage } from '../utils/getNextPage';
import { ProductType } from '../application/models/NewJoinSessionData';
import { RemoveWarningModal, RemoveWarningModalContentProps } from '../components/RemoveWarningModal';
import { TooltipModal, TooltipModalContentProps, TooltipModalSubContent } from '../components/TooltipModal';
import { historyPushState, getProgressProps, formatAddressLine } from '../utils/helpers';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { TotalCostHeader } from '../components/CustomizeMembershipComponents/TotalCostHeader';
import { UtagType } from '../utils/UtagHelper';
import { sources } from '../api/sources';
import { hashStringWithSHA256 } from '../utils/hash';
import { ERSAddOn } from '../components/CustomizeMembershipComponents/ERSAddOn';
import { RVAddOn } from '../components/CustomizeMembershipComponents/RVAddOn';
import { BenefitsPage } from './BenefitsPage';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { ContactSupportCard } from '../components/ContactSupportCard';
import { BenefitLevelCard } from '../components/CustomizeMembershipComponents/BenefitLevelCard';
import { FamilyMemberAddOn } from '../components/CustomizeMembershipComponents/FamilyMemberAddOn';
import { FlowType } from '@ace-clublabs/common-membership-api-types';

export const BuildMembershipPage: React.FC = () => {
  const {
    membershipInfo,
    sessionData,
    updateAssociates,
    refreshSession,
    loadingInfo,
    isPriceCartLoading,
    sendTag,
    getNewBusinessCost,
  } = useContext(MembershipAppStateContext);
  const history = useHistory();
  const [selectedRV, setSelectRV] = useState(sessionData.formData.products?.includes(ProductType.RV) ? true : false);
  const [selectedERS, setSelectedERS] = useState(
    sessionData.formData.products?.includes(ProductType.ERS) ? true : false
  );
  const [isTagging, setIsTagging] = useState(true);
  const [deleteInfo, setDeleteInfo] = useState<RemoveWarningModalContentProps | null>(null);
  const [tooltipInfo, setTooltipInfo] = useState<TooltipModalContentProps | null>(null);
  const [tooltipContent, setTooltipContent] = useState('');
  const [memAddOnTooltipInfo, setMemAddOnTooltipInfo] = useState<TooltipModalContentProps | null>(null);

  const currentUrl = window.location.href;
  const urlSearchParams = new URL(currentUrl).searchParams;

  const isChangingMembershipLevelParam = urlSearchParams.get('isChangingMembershipLevel') === 'true';
  const [isChangingMembershipLevel, setIsChangingMembershipLevel] = useState(isChangingMembershipLevelParam);

  useEffect(() => {
    setIsChangingMembershipLevel(isChangingMembershipLevelParam);
  }, [isChangingMembershipLevelParam]);

  const showBenefitPage = () => {
    urlSearchParams.set('isChangingMembershipLevel', 'true');
    const newUrl = currentUrl.split('?')[0] + '?' + urlSearchParams.toString();
    historyPushState({ path: newUrl }, '', newUrl);
    setIsChangingMembershipLevel(true);
  };

  const closeBenefitsPage = () => {
    window.history.back();
  };

  const isGiftFlow = sessionData.flowType === FlowType.GIFT;

  const progressProps = getProgressProps(sessionData.flowType!, Pages.BuildMembershipPage);

  const tag = async () => {
    const { firstName, lastName, gender, residenceAddress, email, mobileNumber } = sessionData.formData;
    const { state, city, zipCode } = residenceAddress;
    const formattedStreetAddress = formatAddressLine(residenceAddress);

    const encryptedTags = await sources.encryptAnalytics({
      ed_olm_1: firstName,
      ed_olm_2: lastName,
      ed_olm_3: gender,
      ed_olm_4: formattedStreetAddress,
      ed_olm_5: city,
      ed_olm_6: state,
      ed_olm_7: zipCode,
      ed_olm_8: email,
      ed_olm_9: mobileNumber,
    });

    const overrides =
      sessionData.flowType !== FlowType.GIFT
        ? {
            ...encryptedTags,
            hd_olm_1: hashStringWithSHA256(email!),
            hd_olm_2: hashStringWithSHA256(mobileNumber!),
          }
        : {};
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:add ons' : 'add ons';

    sendTag(UtagType.view, tagPage, overrides);
    setIsTagging(false);
  };

  useEffect(() => {
    if (!sessionData) {
      return;
    }
    setSelectRV(sessionData.formData.products?.includes(ProductType.RV) ? true : false);
    setSelectedERS(sessionData.formData.products?.includes(ProductType.ERS) ? true : false);
  }, [sessionData.formData.products]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo) {
      return;
    }

    tag();
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null || isTagging;
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </Container>
    );
  }

  return isChangingMembershipLevel ? (
    <BenefitsPage onContinue={closeBenefitsPage} isUpgrading={true} />
  ) : (
    <Box className="build-membership-view">
      <AppProgressHeader {...progressProps} />
      <TotalCostHeader />
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#F7F8FA',
          marginTop: '1.625rem',
          padding: '1.5rem 0',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#F7F8FA',
            zIndex: -1,
            width: '100vw',
          },
        }}
      >
        <BenefitLevelCard />
        <FamilyMemberAddOn />
        <RVAddOn
          setTooltipInfo={setTooltipInfo}
          setTooltipContent={setTooltipContent}
          setDeleteInfo={setDeleteInfo}
          selectedRV={selectedRV}
          onUpgradeClick={showBenefitPage}
        />

        {isGiftFlow ? (
          <></>
        ) : (
          <ERSAddOn
            setTooltipInfo={setTooltipInfo}
            setTooltipContent={setTooltipContent}
            setDeleteInfo={setDeleteInfo}
            selectedERS={selectedERS}
          />
        )}

        <Box className="review-page-btn">
          <ButtonGroup>
            <Button
              id="nextButton"
              onClick={() => {
                history.push(getNextPage(Pages.BuildMembershipPage, sessionData.flowType));
              }}
              type="submit"
              className="btn-large"
              label={
                <>
                  {isPriceCartLoading ? (
                    <Box className="button-spinner">
                      <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                    </Box>
                  ) : (
                    <></>
                  )}
                  Next
                </>
              }
              disabled={isPriceCartLoading}
            />
          </ButtonGroup>
        </Box>
      </Box>
      <ContactSupportCard />
      <RemoveWarningModal
        title={deleteInfo?.title ?? ''}
        warningContent={deleteInfo?.warningContent ?? ''}
        showModal={!!deleteInfo}
        closeModal={() => setDeleteInfo(null)}
        confirmRemove={() => {
          deleteInfo?.confirmRemove();
        }}
      />

      <TooltipModal
        tooltipTitle={tooltipInfo?.tooltipTitle ?? ''}
        showModal={!!tooltipInfo}
        closeModal={() => {
          setTooltipInfo(null);
          setTooltipContent('');
        }}
      >
        <TooltipModalSubContent tooltipContent={tooltipContent}></TooltipModalSubContent>
      </TooltipModal>
    </Box>
  );
};
